
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useReferral } from "@/hooks/useReferral";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['auth', 'common']);
  const { processStoredReferral } = useReferral();
  
  const referralToken = searchParams.get('token');
  const legacyReferralCode = searchParams.get('ref');
  const fromInvite = searchParams.get('from') === 'invite';

  // Show invitation message if from invite
  useEffect(() => {
    if (fromInvite) {
      toast.info('Please log in to accept the invitation and connect with friends.');
    }
  }, [fromInvite]);

  // Process referral and redirect when user becomes authenticated
  useEffect(() => {
    if (!loading && user) {
      console.log('Login - User authenticated, processing referral if needed');
      
      // Process stored referral if available
      if (processStoredReferral && user.id) {
        processStoredReferral(user.id)
          .then((referrerProfileId) => {
            if (referrerProfileId) {
              console.log('Stored referral processed for user, referrer:', referrerProfileId);
              toast.success('Welcome back! Check your friend recommendations.');
            }
          })
          .catch((error) => {
            console.error('Error processing stored referral:', error);
          });
      }
      
      // Navigate appropriately
      if (fromInvite) {
        navigate('/friends?tab=requests&from=invite', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, loading, navigate, fromInvite, processStoredReferral]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await signIn(email, password);
      console.log('Login successful - user state will handle navigation and referral processing');
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen bg-uplivion-lightBlue/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-uplivion-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 flex flex-col items-center justify-center p-4">
      <a href="https://www.uplivion.life" className="mb-8">
        <img 
          src="/uplivion-logo.svg" 
          alt="uplivion" 
          className="h-12 w-auto"
        />
      </a>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">{t('auth:login.title')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth:fields.email')}</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder={t('auth:placeholders.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required 
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t('auth:fields.password')}</Label>
              <Link 
                to="/forgot-password" 
                className="text-xs text-uplivion-blue hover:underline"
              >
                {t('auth:login.forgotPassword')}
              </Link>
            </div>
            <Input 
              id="password" 
              type="password" 
              placeholder={t('auth:fields.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required 
            />
          </div>
          
          <Button 
            type="submit"
            className="w-full bg-uplivion-blue hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common:labels.loading')}
              </>
            ) : (
              t('common:buttons.login')
            )}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('auth:login.noAccount')} {" "}
            <Link 
              to={referralToken ? `/register?token=${referralToken}` : legacyReferralCode ? `/register?ref=${legacyReferralCode}` : "/register"}
              className="text-uplivion-blue hover:underline font-medium"
            >
              {t('auth:login.registerLink')}
            </Link>
          </p>
        </div>
      </div>
      
      <div className="mt-6">
        <Button variant="outline" asChild>
          <a href="https://www.uplivion.life">
            {t('common:buttons.backToWebsite')}
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Login;
